import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, UserPlus, X, Search, Loader2, Trash2, AlertCircle, Globe,
  Mail, RefreshCw, ChevronRight, Inbox,
} from 'lucide-react';
import MailchimpLibrary from './MailchimpLibrary';
import PeopleHub from './PeopleHub';
import CurateList from './CurateList';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

function StatPill({ label, value, accent = '#48B64C', testid }) {
  return (
    <div data-testid={testid} className="bg-hg-surface border border-hg-border rounded-sm p-4">
      <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">{label}</div>
      <div className="font-display text-2xl text-hg-text tracking-wider mt-2" style={{ color: value === 0 ? '#888' : accent }}>{value}</div>
    </div>
  );
}

function AddSubscriberModal({ audiences, onClose, onAdded }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [groups, setGroups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggleGroup = (label) =>
    setGroups((g) => g.includes(label) ? g.filter((x) => x !== label) : [...g, label]);

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/subscribers`, { email, name, groups });
      onAdded(res.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add subscriber');
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-hg-surface border border-hg-border rounded-sm w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-subscriber-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg text-hg-text tracking-wider">ADD SUBSCRIBER</h3>
          <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1.5">Email</label>
            <input
              data-testid="add-sub-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
            />
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1.5">Name (optional)</label>
            <input
              data-testid="add-sub-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First Last"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
            />
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1.5">Add to Groups</label>
            <div className="space-y-1.5">
              {audiences.map((a) => {
                const checked = groups.includes(a.label);
                return (
                  <button
                    key={a.label}
                    data-testid={`add-sub-group-${a.label.replace(/[^a-z0-9]/gi, '_')}`}
                    onClick={() => toggleGroup(a.label)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs font-sans rounded-sm border transition-colors text-left ${
                      checked ? 'border-hg-green bg-hg-green/10 text-hg-text' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
                    }`}
                  >
                    <span>{a.label}</span>
                    <span className="text-[10px] text-hg-text-dim">{a.subscribers} subs</span>
                  </button>
                );
              })}
            </div>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-2 flex items-start gap-2">
              <AlertCircle size={12} className="text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-[11px] text-red-400 font-sans">{error}</span>
            </div>
          )}
          <button
            data-testid="add-sub-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-hg-green text-hg-text text-sm font-sans font-medium py-2.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            {submitting ? 'Adding…' : 'Add Subscriber'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Subscribers() {
  const [tab, setTab] = useState('people');
  return (
    <div className="space-y-4" data-testid="subscribers-page-root">
      <div className="flex items-center gap-1 border-b border-hg-border">
        <button
          data-testid="tab-people"
          onClick={() => setTab('people')}
          className={`px-4 py-2 text-xs font-sans uppercase tracking-[0.18em] border-b-2 transition-colors ${
            tab === 'people' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >People (Live)</button>
        <button
          data-testid="tab-curate"
          onClick={() => setTab('curate')}
          className={`px-4 py-2 text-xs font-sans uppercase tracking-[0.18em] border-b-2 transition-colors ${
            tab === 'curate' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >Curate List</button>
        <button
          data-testid="tab-mailerlite"
          onClick={() => setTab('mailerlite')}
          className={`px-4 py-2 text-xs font-sans uppercase tracking-[0.18em] border-b-2 transition-colors ${
            tab === 'mailerlite' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >Website Signups</button>
        <button
          data-testid="tab-mailchimp"
          onClick={() => setTab('mailchimp')}
          className={`px-4 py-2 text-xs font-sans uppercase tracking-[0.18em] border-b-2 transition-colors ${
            tab === 'mailchimp' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >Mailchimp Archive</button>
      </div>
      {tab === 'people' && <PeopleHub />}
      {tab === 'curate' && <CurateList />}
      {tab === 'mailerlite' && <MailerliteSubscribers />}
      {tab === 'mailchimp' && <MailchimpLibrary />}
    </div>
  );
}

function MailerliteSubscribers() {
  const [audiences, setAudiences] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeGroup, setActiveGroup] = useState('all');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState('');

  const loadAudiences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscribers/audiences`);
      setAudiences(res.data.audiences || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadSubs = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (activeGroup !== 'all') params.set('group', activeGroup);
      if (search) params.set('search', search);
      params.set('limit', '50');
      const res = await axios.get(`${API}/subscribers?${params.toString()}`);
      setSubs(res.data.subscribers || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load subscribers');
    }
    setLoading(false);
    setRefreshing(false);
  }, [activeGroup, search]);

  const loadFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscribers/signup-feed?limit=10&days=30`);
      setFeed(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadAudiences(); loadFeed(); }, [loadAudiences, loadFeed]);
  useEffect(() => { loadSubs(); }, [loadSubs]);

  const handleDelete = async (sub) => {
    if (!confirm(`Unsubscribe ${sub.email}?`)) return;
    setActionId(sub.id);
    try {
      await axios.delete(`${API}/subscribers/${sub.id}`);
      setSubs((curr) => curr.filter((s) => s.id !== sub.id));
      loadAudiences();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
    setActionId('');
  };

  const handleAdded = () => {
    loadSubs(false);
    loadAudiences();
  };

  const totalActive = audiences.reduce((a, b) => a + (b.subscribers || 0), 0);

  return (
    <div className="space-y-6" data-testid="subscribers-page">
      {showAdd && <AddSubscriberModal audiences={audiences} onClose={() => setShowAdd(false)} onAdded={handleAdded} />}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">SUBSCRIBERS</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">Mailerlite-backed audiences for newsletters &amp; email campaigns.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="subs-refresh-btn"
            onClick={() => { setRefreshing(true); loadSubs(false); loadAudiences(); loadFeed(); }}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            data-testid="subs-add-btn"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors"
          >
            <UserPlus size={12} /> Add Subscriber
          </button>
        </div>
      </div>

      {/* Audience pills */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatPill testid="stat-total" label="Total Active" value={totalActive} />
        {audiences.map((a) => (
          <StatPill key={a.group_id} label={a.label} value={a.subscribers || 0} testid={`stat-${a.label.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`} />
        ))}
      </div>

      {/* Website signups feed */}
      {feed && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3" data-testid="webhook-feed">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Globe size={14} className="text-hg-green" strokeWidth={1.5} />
              <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">Website Signups (henry-griffitts.com)</h3>
            </div>
            <div className="flex items-center gap-4 text-xs font-sans">
              <span className="text-hg-text-dim">Today: <span className="text-hg-text">{feed.count_today}</span></span>
              <span className="text-hg-text-dim">Last 7 days: <span className="text-hg-text">{feed.count_7d}</span></span>
            </div>
          </div>
          {feed.items && feed.items.length > 0 ? (
            <div className="space-y-1">
              {feed.items.map((it, i) => (
                <div key={i} className="bg-hg-bg border border-hg-border rounded-sm px-3 py-2 flex items-center justify-between gap-3 text-xs font-sans">
                  <div className="min-w-0 flex-1">
                    <div className="text-hg-text truncate">{it.name || it.email}</div>
                    <div className="text-[10px] text-hg-text-dim truncate">{it.email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-hg-green/10 text-hg-green uppercase tracking-wider">{it.source}</span>
                    <span className="text-[10px] text-hg-text-dim">{new Date(it.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 gap-2">
              <Inbox size={20} className="text-hg-text-dim" strokeWidth={1.5} />
              <p className="text-xs text-hg-text-dim font-sans">No website signups yet. Wire the webhook into your site to start auto-importing.</p>
              <p className="text-[10px] text-hg-text-dim font-sans">Endpoint: <code className="text-hg-green">POST /api/public/subscribe</code></p>
            </div>
          )}
        </div>
      )}

      {/* Group filter + search */}
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
        <div className="flex flex-wrap gap-1.5 flex-1">
          {[{ label: 'All', value: 'all' }, ...audiences.map((a) => ({ label: a.label, value: a.label }))].map((opt) => (
            <button
              key={opt.value}
              data-testid={`group-filter-${opt.value.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`}
              onClick={() => setActiveGroup(opt.value)}
              className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
                activeGroup === opt.value ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
              }`}
            >{opt.label}</button>
          ))}
        </div>
        <div className="relative">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-hg-text-dim" />
          <input
            data-testid="subs-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or name…"
            className="w-full lg:w-72 bg-hg-bg border border-hg-border rounded-sm pl-8 pr-3 py-2 text-xs text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
          />
        </div>
      </div>

      {/* Subscriber list */}
      <div className="bg-hg-surface border border-hg-border rounded-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-hg-border">
          <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">{activeGroup === 'all' ? 'All Subscribers' : activeGroup}</h3>
          <span className="text-[10px] text-hg-text-dim font-sans">{subs.length} loaded</span>
        </div>

        {error && (
          <div className="m-5 bg-red-500/10 border border-red-500/30 rounded-sm p-3 flex items-start gap-2">
            <AlertCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-red-400 font-sans">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
        ) : subs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Users size={24} className="text-hg-text-dim" strokeWidth={1.5} />
            <p className="text-sm text-hg-text-dim font-sans">No subscribers in this view.</p>
          </div>
        ) : (
          <div data-testid="subs-list">
            {subs.map((s) => (
              <div key={s.id} data-testid={`sub-row-${s.id}`} className="border-b border-hg-border/40 last:border-b-0 px-5 py-3 flex items-center justify-between gap-3 hover:bg-hg-bg/40">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Mail size={12} className="text-hg-text-dim flex-shrink-0" />
                    <span className="text-sm font-sans text-hg-text truncate">{s.email}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                      s.status === 'active' ? 'bg-hg-green/10 text-hg-green' : 'bg-hg-border/40 text-hg-text-dim'
                    }`}>{s.status}</span>
                  </div>
                  {s.name && <div className="text-[11px] text-hg-text-dim font-sans mt-0.5 ml-5">{s.name}</div>}
                  {s.groups && s.groups.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5 ml-5">
                      {s.groups.map((g) => (
                        <span key={g.id} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-hg-border/40 text-hg-text-dim flex items-center gap-1">
                          {g.name}
                          <ChevronRight size={8} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  data-testid={`sub-delete-${s.id}`}
                  onClick={() => handleDelete(s)}
                  disabled={actionId === s.id}
                  className="flex items-center gap-1 px-2 py-1 text-[11px] font-sans text-hg-text-dim hover:text-red-400 transition-colors disabled:opacity-40"
                >
                  {actionId === s.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
